import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  inject,
  Input,
  Output,
} from '@angular/core';
import { TrackProductService } from '@analytics/events';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import { mapOfferToCommerce } from '@innogy/become-a-customer/shared';
import { ENVIRONMENT_CONFIG, isBrandSegmentType } from '@core/config-angular';
import type {
  EnvConfigBrand,
  EnvConfigSegment,
  EnvConfigType,
} from '@core/config-models';
import { EnvironmentConfig } from '@core/config-models';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { Subject, takeUntil } from 'rxjs';
import { ContractType, ProductDuration } from '@essent/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { DatalayerService } from '@analytics/datalayer';
import { AutomationIdDirective } from '@innogy/utils-automation-id';

import {
  CommodityOfferAlignment,
  CommodityOfferContent,
  type CommodityOfferLayoutType,
} from '../commodity-offers.model';
import { CommodityOfferFilterComponent } from '../commodity-offer-filter/commodity-offer-filter.component';
import { EdConsumerAcquisitionCommodityOfferComponent } from '../commodity-offer-brand/ed-consumer-acquisition-commodity-offer/ed-consumer-acquisition-commodity-offer.component';
import { EssentSMEAcquisitionCommodityOfferComponent } from '../commodity-offer-brand/essent-sme-acquisition-commodity-offer/essent-sme-acquisition-commodity-offer.component';
import { EssentConsumerRetentionMinimalCommodityOfferComponent } from '../commodity-offer-brand/essent-consumer-retention-minimal-commodity-offer/essent-consumer-retention-minimal-commodity-offer.component';
import { EssentConsumerRetentionCommodityOfferComponent } from '../commodity-offer-brand/essent-consumer-retention-commodity-offer/essent-consumer-retention-commodity-offer.component';
import { EdConsumerRetentionMinimalCommodityOfferComponent } from '../commodity-offer-brand/ed-consumer-retention-minimal-commodity-offer/ed-consumer-retention-minimal-commodity-offer.component';
import { EdConsumerRetentionCommodityOfferComponent } from '../commodity-offer-brand/ed-consumer-retention-commodity-offer/ed-consumer-retention-commodity-offer.component';
import { EssentSMERetentionCommodityOfferComponent } from '../commodity-offer-brand/essent-sme-retention-commodity-offer/essent-sme-retention-commodity-offer.component';
import { EssentConsumerAcquisitionCommodityOfferComponent } from '../commodity-offer-brand/essent-consumer-acquisition-commodity-offer/essent-consumer-acquisition-commodity-offer.component';

@Component({
  selector: 'wl-commodity-offer-list',
  templateUrl: './commodity-offer-list.component.html',
  styleUrls: [
    './commodity-offer-list.component.ed.scss',
    './commodity-offer-list.component.essent.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    CommodityOfferFilterComponent,
    AutomationIdDirective,
    EdConsumerAcquisitionCommodityOfferComponent,
    EssentSMEAcquisitionCommodityOfferComponent,
    EssentConsumerRetentionMinimalCommodityOfferComponent,
    EssentConsumerRetentionCommodityOfferComponent,
    EdConsumerRetentionMinimalCommodityOfferComponent,
    EdConsumerRetentionCommodityOfferComponent,
    EssentSMERetentionCommodityOfferComponent,
    EssentConsumerAcquisitionCommodityOfferComponent,
  ],
})
export class CommodityOfferListComponent
  implements OnInit, OnChanges, OnDestroy
{
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() offers?: Offer[];
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() content?: CommodityOfferContent;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showMemberGetMember = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() alignment?: CommodityOfferAlignment;
  @Output() selectOffer = new EventEmitter<Offer>();
  @Output() openMoreInfo = new EventEmitter<Offer>();
  @Output() viewTariffs = new EventEmitter<Offer>();
  brand?: EnvConfigBrand;
  segment?: EnvConfigSegment;
  type?: EnvConfigType;
  computedVariant?: CommodityOfferLayoutType;
  selectedContractType?: ContractType;
  _showTariffsForAllOffers = false;

  private readonly onDestroy$ = new Subject();

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly datalayer = inject(DatalayerService);

  protected readonly isBrandSegmentType = isBrandSegmentType;

  constructor(
    @Inject(ENVIRONMENT_CONFIG) private readonly envConfig: EnvironmentConfig,
    private readonly mediaObserverService: MediaObserverService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly trackProductService: TrackProductService
  ) {}

  ngOnInit(): void {
    this.brand = this.envConfig.brand;
    this.segment = this.envConfig.segment;
    this.type = this.envConfig.type;

    this.mediaObserverService.deviceInfo$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ isDesktop }) => {
        if (this.alignment === 'vertical' || !isDesktop) {
          this.computedVariant = 'accordion';
        } else {
          this.computedVariant = 'card';
        }

        // Manually trigger change detection
        this.changeDetectorRef.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const newOffers = changes['offers']?.currentValue as Offer[];

    if (newOffers && newOffers.length > 0) {
      // Track impressions of all shown offers
      newOffers.forEach((offer) => {
        const commerceObject = mapOfferToCommerce(offer);
        this.trackProductService.trackImpression(commerceObject);
      });

      // If no contract type is selected, select the query param or the first available contract type
      if (!this.selectedContractType) {
        const contractType =
          this.activatedRoute.snapshot.queryParams['contractType'];
        const isValidContractType =
          this.availableContractTypes.includes(contractType);
        this.selectedContractType =
          contractType && isValidContractType
            ? contractType
            : this.availableContractTypes[0];

        if (!isValidContractType) {
          this.updateContractTypeQueryParam(null);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
    this.updateContractTypeQueryParam(null);
  }

  private readonly isEssentOrEDMijnConsumer = () =>
    isBrandSegmentType({
      brand: ['essent', 'ed'],
      segment: 'consument',
      type: 'mijn',
      currentBrand: this.brand,
      currentSegment: this.segment,
      currentType: this.type,
    });

  updateContractTypeQueryParam(contractType: ContractType | null) {
    const url = this.router
      .createUrlTree([], {
        queryParams: {
          contractType,
        },
        queryParamsHandling: 'merge',
      })
      .toString();
    this.location.replaceState(url);
  }

  get showTariffsForAllOffers() {
    return this._showTariffsForAllOffers && this.computedVariant === 'card';
  }

  get listContainsHighlights() {
    return !!this.offers?.some(
      (offer) => offer.isHighlighted && offer.isHighlightedLabel
    );
  }

  get showFilters() {
    return (
      this.isEssentOrEDMijnConsumer() && this.availableContractTypes.length > 1
    );
  }

  get listContainsIncentives() {
    return !!this.offers?.some((offer) => offer.incentiveId);
  }

  get availableContractTypes() {
    const orderedContractTypes = [
      ContractType.FIXED,
      ContractType.VARIABLE,
      ContractType.DYNAMIC,
    ];

    const contractTypesSet = Array.from(
      new Set(this.offers?.map((offer) => this.detectOfferContractType(offer)))
    );

    return orderedContractTypes.filter((contractType) =>
      contractTypesSet.some((type) => type === contractType)
    );
  }

  prioritiseOffers(offers: Offer[]): Offer[] {
    const activeOfferIndex =
      Math.floor(offers.length / 2) + (offers.length % 2);

    /**
     * In the new commodity offer listings, the middle offer shown is the most important. We therefore switch the first offer to the middle.
     * This workaround must be deleted or at least reworked after the new commodity offers permanently replace the existing offer pages.
     * @see: https://jira.essent.nl/jira/browse/CTR-848
     */
    if (offers.length >= 2) {
      return [
        ...offers.slice(1, activeOfferIndex),
        offers[0],
        ...offers.slice(activeOfferIndex),
      ];
    } else {
      return offers;
    }
  }

  get prioritizedOffers() {
    if (
      this.isEssentOrEDMijnConsumer() ||
      isBrandSegmentType({
        brand: ['essent'],
        segment: 'zakelijk',
        type: 'mijn',
        currentBrand: this.brand,
        currentSegment: this.segment,
        currentType: this.type,
      })
    ) {
      return this.filteredOffers;
    } else {
      return this.prioritiseOffers(this.filteredOffers);
    }
  }

  isActive(offer: Offer): boolean {
    return this.initiallyActiveOfferIds.includes(offer.offerId);
  }

  filterOffers(offers: Offer[]): Offer[] {
    if (this.isEssentOrEDMijnConsumer()) {
      return offers.filter(
        (offer) =>
          this.detectOfferContractType(offer) === this.selectedContractType
      );
    } else {
      return offers;
    }
  }

  get filteredOffers() {
    return this.filterOffers(this.offers ?? []);
  }

  get initiallyActiveOfferIds() {
    return [this.filteredOffers[0].offerId];
  }

  detectOfferContractType(offer: Offer): ContractType {
    if (offer.duration === ProductDuration.FLEX && offer.isDynamicProduct) {
      return ContractType.DYNAMIC;
    } else if (
      offer.duration === ProductDuration.FLEX &&
      !offer.isDynamicProduct
    ) {
      return ContractType.VARIABLE;
    } else {
      return ContractType.FIXED;
    }
  }

  changeContractType(contractType: ContractType) {
    if (this.selectedContractType !== contractType) {
      this.datalayer.pushEvent({
        event: 'filter-clickonresult',
        info: {
          name: 'Retentie aanbod filter',
          filter: contractType,
        },
      });
    }

    this.selectedContractType = contractType;
    this.updateContractTypeQueryParam(contractType);
  }
}
